/* eslint-disable import/prefer-default-export */
import { useCallback, useEffect, useRef, useState } from 'react';

import { checkDuplicateMovement } from '../services/movements/movementsApi';

// ---------------------------------------------------------------------------
// Warn guard de duplicidade de movimento (handoff 0089; rework 2026-08-16).
//
// Alimenta o indicador ao lado do campo Nº Documento nas telas de criação
// (MovementIn / MovementOut) e fornece a consulta fresca pro modal unificado
// de confirmação do Salvar (`MovementConfirmModal`):
//
// - `triggerCheck` roda quando o quarteto (pdv, tipo doc, nº doc, in/out)
//   completa (blur do nº documento / troca dos selects). Alimenta SÓ o
//   indicador — o detalhe abre pelo "ver" (`reopenModal`), nunca sozinho.
// - `checkNow` é a guarda do Salvar: consulta fresca que RETORNA a lista de
//   duplicatas (sem abrir modal) — o modal unificado da página exibe o achado.
// - DUPLICIDADE NÃO SE CACHEIA (mesma lição do check de saldo): o resultado
//   muda quando outra tela/operador salva um movimento com os mesmos índices.
//   Toda consulta é fresca; só há dedupe de requisições EM VOO da mesma chave.
// - Falha do check (rede/4xx/403) NUNCA bloqueia: vira status `skipped`,
//   silencioso pro usuário (console.warn pra diagnóstico) — recurso
//   informativo (handoff 0089 §4.4).
//
// `status`: idle | checking | clear | found | skipped.
// ---------------------------------------------------------------------------

export function useMovementDuplicateCheck({
   pdvId,
   docType,
   docNumber,
   moveType,
}) {
   const isComplete = Boolean(pdvId && docType && docNumber && moveType);
   const key = isComplete
      ? `${pdvId}|${docType}|${docNumber}|${moveType}`
      : '';

   const [status, setStatus] = useState('idle');
   const [duplicates, setDuplicates] = useState([]);
   const [isModalOpen, setIsModalOpen] = useState(false);

   const inflightRef = useRef(new Map()); // chave → Promise em voo
   const currentKeyRef = useRef(key);

   // Sincroniza o indicador quando a combinação muda (editar o nº doc invalida
   // o "✓" na hora). Sem restauração de resultado antigo — o próximo gatilho
   // (blur / Salvar) re-consulta fresco.
   useEffect(() => {
      currentKeyRef.current = key;
      if (!key) {
         setStatus('idle');
         setDuplicates([]);
         return;
      }
      setStatus(inflightRef.current.has(key) ? 'checking' : 'idle');
      setDuplicates([]);
   }, [key]);

   const applyResultToIndicator = useCallback((k, result) => {
      if (currentKeyRef.current !== k) return; // resposta de combinação antiga
      if (result === 'error') {
         setStatus('skipped');
         setDuplicates([]);
      } else {
         setStatus(result.length ? 'found' : 'clear');
         setDuplicates(result);
      }
   }, []);

   // Consulta SEMPRE fresca — sem cache de resultado. Só deduplica requisição
   // em voo da mesma chave (blur + Salvar no mesmo instante = 1 request).
   const fetchFresh = useCallback(
      (k, params) => {
         if (inflightRef.current.has(k)) {
            return inflightRef.current.get(k);
         }
         if (currentKeyRef.current === k) setStatus('checking');

         const promise = checkDuplicateMovement(params)
            .catch((err) => {
               // Nunca bloqueia nem toasta (0089 §4.4) — mas fica diagnosticável.
               // eslint-disable-next-line no-console
               console.warn('[duplicate-check] consulta falhou:', err);
               return 'error';
            })
            .then((result) => {
               inflightRef.current.delete(k);
               applyResultToIndicator(k, result);
               return result;
            });

         inflightRef.current.set(k, promise);
         return promise;
      },
      [applyResultToIndicator]
   );

   // Gatilho "cedo" (blur do nº documento / troca dos selects com tudo cheio).
   // Alimenta SÓ o indicador — o detalhe abre pelo "ver".
   const triggerCheck = useCallback(() => {
      if (!key) return;
      fetchFresh(key, { pdvId, docNumber, docType, moveType });
   }, [key, pdvId, docNumber, docType, moveType, fetchFresh]);

   // Guarda do Salvar: consulta fresca que retorna `duplicates[]` ([] em
   // falha — não-bloqueante) para o modal unificado de confirmação da página.
   const checkNow = useCallback(async () => {
      if (!key) return [];
      const result = await fetchFresh(key, {
         pdvId,
         docNumber,
         docType,
         moveType,
      });
      return Array.isArray(result) ? result : [];
   }, [key, pdvId, docNumber, docType, moveType, fetchFresh]);

   // Viewer de detalhe, aberto pelo indicador "⚠ N similares — ver".
   const reopenModal = useCallback(() => {
      if (!duplicates.length) return;
      setIsModalOpen(true);
   }, [duplicates]);

   const closeModal = useCallback(() => {
      setIsModalOpen(false);
   }, []);

   // Usado pelo "Limpar" da página.
   const reset = useCallback(() => {
      inflightRef.current.clear();
      setIsModalOpen(false);
      setStatus('idle');
      setDuplicates([]);
   }, []);

   return {
      status,
      duplicates,
      isModalOpen,
      triggerCheck,
      checkNow,
      reopenModal,
      closeModal,
      reset,
   };
}
